/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { ENV } from '@environment';
import { isEmpty } from 'lodash';
import { RootState } from 'store';

import AutoAgencies from 'components/AutoAgencies';
import AutoDetailRates from 'components/AutoDetailRates';
import SimpleAlert from 'components/SimpleAlert';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useUserAccount from 'hooks/useUserAccount';
import { VehicleOption } from 'models/auto/vehicle';
import { VehicleValidateResponse } from 'models/auto/vehicleValidate';
import { amplitudelogEvent, ProceedToCheckoutEvent } from 'services/amplitudeService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount } from 'utils/generic';

import AutoCardDetails from './components/AutoCardDetails';
import Checkout from './components/Checkout';
import { Container, Section, Comments, ContainerRules } from './styles';

interface Props {
  creating: boolean;
  showChurning: boolean;
  setShowChurning: (show: boolean) => void;
  breakRulesMessage?: string[];
  details: VehicleValidateResponse;
  optionId: string;
  onClose: () => void;
  onConfirm: (
    auto: VehicleValidateResponse,
    justifyForBreakingRules: string,
    reason: string,
    costCenterId?: string,
    breakRulesMessageId?: string,
  ) => void;
  list?: VehicleOption[];
  onSelectAgency?: (id?: string) => void;
  show?: boolean;
  unlimitedKm?: boolean;
}

const AutoDetails = ({
  creating,
  showChurning,
  setShowChurning,
  breakRulesMessage,
  details,
  optionId,
  onClose,
  onConfirm,
  list,
  onSelectAgency,
  show = false,
  unlimitedKm = false,
}: Props) => {
  const { t } = useStoreTranslation();
  const [showAgencies, setShowAgencies] = useState(false);
  const [selectedVehicle, setSelectedVehicle] = useState<VehicleOption>();
  const [isPickUp, setIsPickUp] = useState(true);
  const [hideTooltip, setHideTooltip] = useState(false);
  const userAccount = useUserAccount();
  const { pickUpPoint, dropOffPoint, pickUpDate, dropOffDate, search } = useSelector((state: RootState) => state.autos);

  const amplitudeProceedToCheckoutEvent: ProceedToCheckoutEvent = useMemo(
    () => ({
      SOURCE: 'BOOKING',
      TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
      TRAVEL_FROM: pickUpPoint?.code ?? '',
      TRAVEL_TO: dropOffPoint?.code ?? '',
      TRAVEL_DESTINATION_TYPE:
        search?.distanceRestriction && search?.distanceRestriction === 'Domestic' ? 'NATIONAL' : 'INTERNATIONAL',
      TRAVEL_TRIP_TYPE: '',
      TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
      TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
      TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
      TRAVEL_TRIP_LENGTH:
        pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
      TRAVEL_PASSENGERS: '1',
      TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
      SEARCHID: search?.searchId ?? '',
      TRAVEL_PRODUCT_CODE: details?.vehicleOption?.company?.code ?? '',
      TRAVEL_PRODUCT_NAME: details?.vehicleOption?.company?.name ?? '',
      TRAVEL_SUB_PRODUCT: details?.vehicleOption?.vehicle?.model ?? '',
      TRAVEL_PRODUCT_CATEGORY: details?.vehicleOption?.vehicle?.vehicleCategory ?? '',
      TRAVEL_PRODUCT_PRICE:
        details?.vehicleOption?.rates &&
          details?.vehicleOption?.rates[0] &&
          details?.vehicleOption?.rates[0].totalPricePayNow
          ? `${details?.vehicleOption?.rates[0].totalPricePayNow}`
          : '',
      TRAVEL_PRODUCT_PROVIDER: details?.vehicleOption?.provider ?? '',
      TRAVEL_FARE_TYPE: '',
    }),
    [
      details?.vehicleOption,
      dropOffDate,
      dropOffPoint?.code,
      pickUpDate,
      pickUpPoint?.code,
      search?.distanceRestriction,
      search?.searchId,
    ],
  );

  const handleConfirm = useCallback(
    (justifyForBreakingRules: string, reason: string, costCenterId?: string, breakRulesMessageId?: string) => {
      setHideTooltip(true);

      if (!details) return;

      amplitudelogEvent(amplitudeEvents.PROCEED_TO_CHECKOUT, amplitudeProceedToCheckoutEvent, userAccount);

      onConfirm(details, justifyForBreakingRules, reason, costCenterId, breakRulesMessageId);
    },
    [amplitudeProceedToCheckoutEvent, details, onConfirm, userAccount],
  );

  const handleAgencies = (_isPickUp: boolean) => {
    setIsPickUp(_isPickUp);
    setShowAgencies(true);
  };

  const handleContinue = () => {
    setHideTooltip(true);
  };

  const hasOptions = (list?: VehicleOption[]) => {
    if (list && list.length > 0) {
      const options = list?.filter((item) => item.optionId !== optionId);
      return options.length > 0 ? true : false;
    }
    return false;
  };

  const commentsContract = useMemo(
    () =>
      details.vehicleOption.commentContracts ? (
        <Comments>
          <div dangerouslySetInnerHTML={{ __html: details.vehicleOption.commentContracts }} />
        </Comments>
      ) : undefined,
    [details.vehicleOption.commentContracts],
  );

  useEffect(() => {
    if (list && optionId) {
      const vehicle = list?.find((item) => item.optionId && `${item.optionId}` === optionId);
      if (vehicle) setSelectedVehicle(vehicle);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const price = useMemo(
    () =>
      details.vehicleOption.rates && details.vehicleOption.rates[0] && details.vehicleOption.rates[0].price
        ? details.vehicleOption.rates[0].price
        : {},
    [details],
  );

  const translateMessage = useCallback(
    (msg) => {
      const text = `rulesMessage.${msg}`;
      return t(text) !== text ? t(text) : msg;
    },
    [t],
  );

  const total = useMemo(
    () =>
      details.vehicleOption.rates &&
        details.vehicleOption.rates[0] &&
        details.vehicleOption.rates[0].taxesPayableAtDestination
        ? details.vehicleOption.rates[0].totalPricePayNow
        : undefined,
    [details],
  );

  return show ? (
    <Container>
      {breakRulesMessage && breakRulesMessage.length > 0 && (
        <ContainerRules>
          <SimpleAlert
            title={t('autosDetails.outOfPolicies')}
            content={
              <ul>
                {breakRulesMessage.map((msg) => (
                  <li key={msg}>&bull; {translateMessage(msg)}</li>
                ))}
              </ul>
            }
          />
        </ContainerRules>
      )}
      {details.vehicleOption && (
        <AutoCardDetails
          data={details.vehicleOption}
          hasOptions={hasOptions(list)}
          handleAgencies={handleAgencies}
          isDetails={true}
          unlimitedKm
        >
          {commentsContract}
        </AutoCardDetails>
      )}
      {!isEmpty(price) && details.vehicleOption?.pickUp && details.vehicleOption?.dropOff && (
        <>
          <Section.Last>
            <AutoDetailRates
              rates={price}
              pickUp={new Date(`${details.vehicleOption?.pickUp.time}`)}
              dropOff={new Date(`${details.vehicleOption?.dropOff.time}`)}
              total={total}
              hideTooltip={hideTooltip}
            />
          </Section.Last>
          <Checkout
            loading={creating}
            isBestOption={false}
            onConfirm={handleConfirm}
            onContinue={handleContinue}
            onContinueClose={() => setHideTooltip(false)}
            onClose={onClose}
            price={{
              amount: price.amount || 0,
              commissionAmount: price.commissionAmount || 0,
              currencyCode: price.currencyCode || 'COP',
              discountAmount: price.discountAmount || 0,
              fees: price.fees || 0,
              penaltyAmount: price.penaltyAmount || 0,
              surcharges: price.surcharges || 0,
              taxesValue: price.taxesValue || 0,
              total: price.total || 0,
            }}
            isPreApproved={true}
            showChurning={showChurning}
            setShowChurning={setShowChurning}
            details={details}
          />
        </>
      )}

      <AutoAgencies
        agencies={list}
        show={showAgencies}
        onClose={() => setShowAgencies(false)}
        isLoading={false}
        isPickUp={isPickUp}
        onSelect={(option) => {
          setShowAgencies(false);
          if (onSelectAgency) onSelectAgency(String(option.optionId));
        }}
        selectedVehicle={selectedVehicle}
      />
    </Container>
  ) : null;
};

export default AutoDetails;
